<template lang="pug">
div.drop.message-input-wrapper(v-if="showTextInput" @drop="(item) => attachItem(item)")
  .message-input-content(
    ref="input-container",
    :class="{ 'with-attachment': attachedFiles.length !== 0 }"
  )
    .message-input.scroll-small.show-on-hover(
      :id="$_IDS.CHAT_INPUT",
      :class="{ 'prevent-cursor': !messagePermission }"
      :contenteditable="messagePermission",
      @keydown.enter.stop.prevent="",
      @keydown="systemKeysListener",
      @input="onInput",
      @focus="onFocus",
      @blur="onBlur",
      @paste="onPaste",
      @keyup="refreshCaret",
      @mouseup="refreshCaret",
      :placeholder="placeholder",
      :data-ghost="visibleGhost"
    )
  .items-attached(:class="{ 'is-hidden': !hasAttachments }")
    .images-content
      .image-item.attached-file(v-for="item in attachedFiles" :key="item.key")
        i.iq-ico-close.image-remove(@click="removeFile(item)")
        aiq-Image.image-item_img-preview(v-if="isFileImage(item)"
                                         :key="item.key"
                                         :imageKey="item.key"
                                         :src="item.url"
                                         :acl="item.acl"
                                         :bucket="item.bucket")
        aiq-document-icon(v-else
                          :mimeType="getMimeType(item)"
                          :documentExtension="getDocExt(item)"
                          :fileId="''+item.id"
                          width="64px"
                          height="auto")
        .attachement-name {{ getFileName(item) }}
</template>

<script>
/**
   * User types in this ChatInput component and the typed text is synced with the store.
   * ChatInput's ancestors will then react to these values. Meanwhile, this ChatInput
   * component only updates its input text when
   *   1) watches store for any selected values and sets to that value
   *   2) watches store for changes in selected conversation and will clear the input text
   */

import { nextTick } from 'vue';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
  mapState,
} from 'vuex';
import some from 'lodash/some';
import {
  getCaretPosition,
} from '@/libs';
import { KEYBOARD_KEYS, IMAGE_ACL_TYPES } from '@/constants';
import { IDS } from '@/pages/Conversations/conversationConstants';
import { AGENT_ASSET, AGENT_DOCUMENT } from '@/constants/analytics';
import axios from 'axios';
import { hosts } from '@/config/api.routes';
import { convertRichTextToChatInput, stripHtmlToPlainText } from '@/libs/htmlParser';

const imgTagRegex = /<img\s+src=\\".*"\s+.*>/i;
const suspiciousClipboardDataTypes = {
  notAllowed: 'Files',
  needsChecking: ['text/plain', 'text/html'],
};

export default {
  name: 'chatInput',
  inject: ['chatEventEmitter', 'focusOnInputbox'],
  props: {
    placeholder: {
      type: String,
    },
    channel: {
      type: String,
      default: '',
    },
    isCommandsOpened: {
      type: Boolean,
      default: () => false,
    },
    selectedResultText: {
      type: String,
    },
    messagePermission: {
      type: Boolean,
      default: () => false,
    },
    showTextInput: {
      type: Boolean,
      default: () => true,
    },
    onImageUpload: {
      type: Function,
      default: () => {},
    },
    onBeforeUpload: {
      type: Function,
      default: () => {},
    },
    onUploadFailure: {
      type: Function,
      default: () => {},
    },
  },
  emits: [
    'resetChatInput',
    'resetSelect',
    'systemKeys',
    'updateInput',
  ],
  data() {
    return {
      systemsKeys: [
        KEYBOARD_KEYS.DOWN_ARROW,
        KEYBOARD_KEYS.TAB,
        KEYBOARD_KEYS.UP_ARROW,
      ],
      url: `${hosts.management}files/upload`,
      dragState: false,
      eventEmitterCallbacks: {},
      // Suggestions V2: ghost text, drawn after the text while the caret is at its end.
      ghostText: '',
      isFocused: false,
      caretAtEnd: false,
      IMAGE_ACL_TYPES,
    };
  },
  computed: {
    ...mapState({
      // textInput: state => state.chat.textInput,
      selectedConversation: state => state.conversations.selected,
      isMobileContext: state => state.settings.isMobileContext,
      suggestionsV2: state => Boolean(state.featureFlags.FEATURE_FLAGS.SUGGESTIONS_V2),
    }),
    attachedFiles() {
      return this.$store.getters['files/attachedFiles'];
    },
    hasAttachments() {
      return !isEmpty(this.attachedFiles);
    },
    visibleGhost() {
      return this.ghostText && this.isFocused && this.caretAtEnd ? this.ghostText : null;
    },
    showEmoji() {
      return process.env.SHOW_EMOJI && this.canView('/conversations/inputbox/message');
    },
  },
  watch: {
    attachedFiles() {
      this.scrollLastImageIntoView();
      this.focusOnInputbox();
    },
  },
  created() {
    this.$_get = (object, key, defaultValue) => get(object, key, defaultValue);
    this.$_IDS = IDS;
  },
  mounted() {
    this.eventEmitterCallbacks = {
      clearInput: this.clearInput.bind(this),
      clearFiles: this.clearFiles.bind(this),
      setText: this.setText.bind(this),
      ghost: (text) => {
        this.ghostText = text || '';
      },
    };
    this.chatEventEmitter.on('ghost', this.eventEmitterCallbacks.ghost);
    this.chatEventEmitter.on('clearInput', this.eventEmitterCallbacks.clearInput);
    this.chatEventEmitter.on('clearFiles', this.eventEmitterCallbacks.clearFiles);
    this.chatEventEmitter.on('setText', this.eventEmitterCallbacks.setText);
    this.chatEventEmitter.on('addEmoji', (emoji) => {
      this.addEmoji(emoji);
    });
  },
  beforeUnmount() {
    this.chatEventEmitter.off('clearInput', this.eventEmitterCallbacks.clearInput);
    this.chatEventEmitter.off('clearFiles', this.eventEmitterCallbacks.clearFiles);
    this.chatEventEmitter.off('setText', this.eventEmitterCallbacks.setText);
    this.chatEventEmitter.off('ghost', this.eventEmitterCallbacks.ghost);

    this.$emit('resetChatInput');
  },
  methods: {
    getInputElem() {
      return this.$el?.querySelector(`#${this.$_IDS.CHAT_INPUT}`);
    },
    getFileName(item) {
      const fallbackFileName = get(item, 'filename', '');
      return get(item, 'name', fallbackFileName);
    },
    getCurrentTextInput(inputElem) {
      if (!inputElem) {
        return '';
      }
      return this.channel === 'sms' ? (inputElem.innerText || '') : (inputElem.innerHTML || '');
    },
    /**
     * Insert plain text at caret in contenteditable (used for SMS paste-as-plain-text).
     */
    insertTextAtCursor(text) {
      const safe = String(text ?? '');
      const inputElem = this.getInputElem();
      const selection = window.getSelection?.();
      if (!selection || !inputElem) {
        return;
      }
      // Ensure we have a valid range in our input (e.g. paste when focused)
      let range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      if (!range || !inputElem.contains(range.commonAncestorContainer)) {
        inputElem.focus();
        range = document.createRange();
        range.selectNodeContents(inputElem);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      range.deleteContents();
      const textNode = document.createTextNode(safe);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
      inputElem.dispatchEvent(new InputEvent('input', { bubbles: true }));
    },
    // Stops the user from pasting images directly into the chat input
    onPaste(event) {
      if (this.channel === 'sms') {
        event.preventDefault();
        const raw = event.clipboardData?.getData('text/plain')
          || event.clipboardData?.getData('text/html')
          || '';
        const plain = stripHtmlToPlainText(raw);
        this.insertTextAtCursor(plain);
        return;
      }
      let containsImgTag = false;

      if (event.clipboardData.types.includes(suspiciousClipboardDataTypes.notAllowed)) {
        containsImgTag = true;
      } else {
        containsImgTag = some(suspiciousClipboardDataTypes.needsChecking, (type) => {
          const pastedHtml = event.clipboardData.getData(type);
          return imgTagRegex.test(pastedHtml);
        });
      }

      if (containsImgTag) {
        event.preventDefault();
        let imageFile = null;
        const clipboardItems = event.clipboardData.items;
        for (const item of clipboardItems) {
          if (item.type.startsWith('image/')) {
            imageFile = item.getAsFile();
            break;
          }
        }

        if (imageFile) {
          this.onBeforeUpload();
          this.uploadImage(imageFile);
        } else this.$aiq.notify.error(this.$t('notification.pasting_images_not_allowed.description'));
      }
    },
    safeGetImage(image, type) {
      if (type === 'asset') {
        return get(image, 'file.payload.thumbnail.url', '');
      } if (type === 'file') {
        return get(image, 'thumbnail.url', '');
      }

      return '';
    },
    attachItem(item) {
      const commonData = {
        conversation_id: this.selectedConversation.id,
      };
      let payload;

      switch (item.type) {
        case 'file':
          payload = {
            id: item.item.file_id,
            title: item.item.name,
          };
          this.$store.dispatch('files/attachFile', { file: item.item, source: 'chatInput' });
          break;
        default:
      }

      this.$store.dispatch('notifications/emitEventToSocket', {
        name: (item.type === 'asset') ? AGENT_ASSET.CLICK : AGENT_DOCUMENT.CLICK,
        payload: {
          ...commonData,
          payload,
        },
      });
    },


    removeAllFiles() {
      if (!isEmpty(this.attachedFiles)) {
        [...this.attachedFiles].forEach(this.removeFile, this);
      }
    },

    removeFile(file) {
      this.$store.dispatch('files/removeAttachedFile', file);
    },

    onInput(event) {
      const textInput = event.target.innerHTML;
      const caretPosition = getCaretPosition(event.target);
      this.updateChat({ textInput, caretPosition });
      this.chatEventEmitter.emit('typing', textInput);
      this.reportComposerText();
    },

    // Suggestions V2: tells the chat panel what the composer holds, after any change.
    reportComposerText() {
      if (!this.suggestionsV2) {
        return;
      }
      const inputElem = this.getInputElem();
      this.chatEventEmitter.emit('composerText', inputElem ? (inputElem.innerText || '') : '');
      this.refreshCaret();
    },

    // Suggestions V2: ghost text shows only while the caret is at the end of the text.
    refreshCaret() {
      if (!this.suggestionsV2) {
        return;
      }
      const inputElem = this.getInputElem();
      const selection = window.getSelection?.();
      if (!inputElem || !selection || !selection.rangeCount || !selection.isCollapsed) {
        this.caretAtEnd = false;
        return;
      }
      const caret = selection.getRangeAt(0);
      if (!inputElem.contains(caret.endContainer)) {
        this.caretAtEnd = false;
        return;
      }
      const afterCaret = document.createRange();
      afterCaret.selectNodeContents(inputElem);
      afterCaret.setStart(caret.endContainer, caret.endOffset);
      this.caretAtEnd = afterCaret.toString().length === 0;
    },

    onFocus() {
      const inputElem = this.getInputElem();
      if (inputElem) {
        inputElem.classList.add('is-focused');
      }
      this.isFocused = true;
      this.$nextTick(this.refreshCaret);
    },

    onBlur() {
      const inputElem = this.getInputElem();
      if (inputElem) {
        inputElem.classList.remove('is-focused');
      }
      this.isFocused = false;
    },

    addEmoji(e) {
      const inputElem = this.getInputElem();
      if (!inputElem) {
        return;
      }
      const existing = this.getCurrentTextInput(inputElem);
      const text = `${existing}${e.native}`;
      this.setText(text);
    },

    clearInput() {
      const inputElem = this.getInputElem();
      if (inputElem) {
        inputElem.innerHTML = '';
      }
      this.reportComposerText();

      this.clearFiles();

      this.updateChat({
        textInput: '',
        caretPosition: -1,
      });
    },

    clearFiles() {
      this.removeAllFiles();
    },

    scrollLastImageIntoView() {
      nextTick(() => {
        const lastImage = this.$el?.querySelector('.image-item:last-of-type');

        if (lastImage) {
          lastImage.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },

    setText(text) {
      this.$nextTick(() => {
        const inputElem = this.getInputElem();
        if (!inputElem) {
          return;
        }

        if (this.channel === 'sms') {
          const plain = stripHtmlToPlainText(text);
          inputElem.innerText = plain;
          this.focusOnInputbox();
          const caretPosition = getCaretPosition(inputElem);
          this.updateChat({ textInput: plain, caretPosition });
          this.reportComposerText();
          return;
        }

        // Check if text contains HTML tags (from RichText editor)
        const isHtml = /<\/?[a-z][\s\S]*>/i.test(text);
        // Convert RichText HTML to ChatInput-compatible HTML that preserves empty paragraphs
        const convertedText = isHtml ? convertRichTextToChatInput(text) : text;
        inputElem.innerHTML = convertedText;
        this.focusOnInputbox();
        const caretPosition = getCaretPosition(inputElem);
        // Store the converted text for updates
        this.updateChat({ textInput: convertedText, caretPosition });
        this.reportComposerText();
      });
    },

    systemKeysListener(event) {
      const inputElem = this.getInputElem();
      if (!inputElem) {
        return;
      }
      const textInput = this.getCurrentTextInput(inputElem);
      let caretPosition = getCaretPosition(inputElem);
      const { key } = event;


      switch (key) {
        case KEYBOARD_KEYS.ENTER:
          if (!event.shiftKey) {
            this.$emit('systemKeys', event);
          }
          break;
        case KEYBOARD_KEYS.LEFT_ARROW:
          caretPosition = caretPosition === 0 ? 0 : caretPosition - 1;
          this.$emit('resetSelect');
          break;
        case KEYBOARD_KEYS.BACKSPACE:
          caretPosition = caretPosition === 0 ? 0 : caretPosition - 1;
          this.$emit('resetSelect');
          break;
        case KEYBOARD_KEYS.RIGHT_ARROW: {
          const len = textInput.length;
          caretPosition = caretPosition === len ? len : caretPosition + 1;

          this.$emit('resetSelect');
          break;
        }
        case KEYBOARD_KEYS.ESCAPE:
          this.clearInput();
          break;
        default:
          if (this.systemsKeys.includes(key)) {
            event.preventDefault();
            this.$emit('systemKeys', event);
            event.stopPropagation();
            if (key === KEYBOARD_KEYS.TAB && this.visibleGhost) {
              // Suggestions V2: Tab accepts the visible ghost text.
              this.chatEventEmitter.emit('acceptGhost');
            }
          }
      }

      this.updateChat({ textInput, caretPosition });
    },
    updateChat(payload) {
      this.$emit('updateInput', payload);
    },
    getDocExt(file) {
      return get(file, 'filename', '')
        .split('.')
        .pop();
    },
    getMimeType(file) {
      return get(file, 'mime', '');
    },
    isFileImage(file) {
      return this.getMimeType(file).includes('image');
    },
    uploadImage(data) {
      const formData = new FormData();
      formData.append('file', data);
      return axios
        .post(this.url,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((res) => {
          this.onImageUpload(res.data);
        })
        .catch((error) => {
          this.onUploadFailure(error);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./../../../../../../styles/aiq-variables.scss";
@import "./../../../../../../styles/media-queries.scss";
@import "./../../../../../../styles/attach-files.scss";

.message-input-wrapper {
  width: 100%;
  @include mobile {
    width: 100%;
  }
  &.dragged-over {
    background: rgba(#000, 0.3);
  }
}

.items-attached {
  background-color: var(--aiq-color-white);
  border: none;
  border-style: none;
}

.message-input-content {
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  /*width: calc(100% - 110px);*/
  min-height: 36px;
  max-height: 275px;
  border: 1px solid $aiq-bdc-message-input;
  background-color: var(--aiq-color-white);
  transition: 0.3s;
  border-bottom: none;
  border-left: none;
  border-right: none;

  &.is-focused {
    background-color: var(--aiq-color-white);
  }
  &.with-attachment {
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
    border-bottom: 0;
  }

  :deep(ul) {
    margin: unset;
    padding: unset;
    padding-left: 1.5rem;

    li {
      list-style: disc;
      list-style-type: initial;
      margin: unset;
      padding: unset;
    }
  }
  :deep(ol) {
    margin: unset;
    padding: unset;
    padding-left: 1.5rem;

    li {
      list-style: decimal;
      margin: unset;
      padding: unset;
    }
  }

  // Preserve empty paragraphs with non-breaking spaces
  :deep(p:only-child) {
    min-height: 1em;
  }
}
.message-input {
  width: 100%;
  padding: 10px 30px 10px 10px;
  color: $aiq-text-message-input;
  font-size: 14px;
  line-height: 17px;
  font-weight: 100;
  white-space: normal;
  word-wrap: break-word;
  word-break: normal;
  outline: none;
  cursor: text;

  &:empty {
    &:before {
      display: block;
      content: attr(placeholder);
      color: $aiq-text-message-input-placeholder;
      font-size: 15px;
      font-weight: 300;
    }
  }

  // Suggestions V2: ghost text, drawn after the text; never part of the message.
  &[data-ghost]::after {
    content: attr(data-ghost);
    // Darker than the placeholder, lighter than typed text.
    color: #9aa0a6;
    pointer-events: none;
  }
  &[data-ghost]:empty::before {
    display: none;
  }
}

.image-item.attached-file {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  .attachement-name {
    font-size: 12px;
    font-weight: 300;
  }
}

.button-emoji {
  position: absolute;
  top: 25px;
  right: 10px;
  min-width: 20px;
  min-height: 20px;
  padding: 0;
  margin: 0;
  line-height: 1px;
  border: 0;
  background: transparent;
  cursor: pointer;

  &[disabled] {
    cursor: not-allowed;
    background-color: transparent;

    [class^="iq-ico"] {
      &::before {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' preserveAspectRatio='xMidYMid' width='20' height='20' viewBox='0 0 18 18'%3E%3Cdefs%3E%3Cstyle%3E.cls-1 %7B fill: %23afafaf; fill-rule: evenodd; %7D%3C/style%3E%3C/defs%3E%3Cpath class='cls-1' d='M15.36 15.36C17.12 13.607 18 11.487 18 9 18 6.513 17.12 4.39 15.36 2.63 13.607.877 11.487 0 9 0 6.513 0 4.39.877 2.63 2.63.877 4.39 0 6.513 0 9 0 11.487.877 13.607 2.63 15.36 4.39 17.12 6.513 18 9 18 11.487 18 13.607 17.12 15.36 15.36M9 1.52C11.067 1.52 12.83 2.25 14.29 3.71 15.75 5.17 16.48 6.933 16.48 9 16.48 11.067 15.75 12.83 14.29 14.29 12.83 15.757 11.067 16.49 9 16.49 6.933 16.49 5.167 15.757 3.7 14.29 2.247 12.83 1.52 11.067 1.52 9 1.52 6.933 2.247 5.17 3.7 3.71 5.167 2.25 6.933 1.52 9 1.52M4.51 10.41C4.397 10.523 4.34 10.667 4.34 10.84 4.347 12.26 4.787 13.337 5.66 14.07L5.67 14.08C6.457 14.767 7.587 15.11 9.06 15.11 10.467 15.11 11.57 14.757 12.37 14.05 13.25 13.297 13.693 12.227 13.7 10.84 13.7 10.667 13.64 10.523 13.52 10.41 13.407 10.29 13.267 10.23 13.1 10.23L4.93 10.23C4.77 10.23 4.63 10.29 4.51 10.41M4.12 7.14C4.02 7.26 3.98 7.403 4 7.57 4.013 7.73 4.087 7.863 4.22 7.97 4.34 8.07 4.48 8.113 4.64 8.1L6.16 7.94C6.32 7.92 6.45 7.847 6.55 7.72 6.657 7.593 6.703 7.447 6.69 7.28 6.67 7.12 6.597 6.987 6.47 6.88 6.343 6.78 6.197 6.74 6.03 6.76L4.52 6.93C4.36 6.943 4.227 7.013 4.12 7.14M13.65 8.28C13.897 8.053 14.02 7.77 14.02 7.43 14.02 7.077 13.897 6.783 13.65 6.55 13.423 6.317 13.14 6.2 12.8 6.2 12.46 6.2 12.167 6.317 11.92 6.55 11.687 6.783 11.57 7.077 11.57 7.43 11.57 7.77 11.687 8.053 11.92 8.28 12.167 8.527 12.46 8.65 12.8 8.65 13.14 8.65 13.423 8.527 13.65 8.28Z'%3E%3C/path%3E%3C/svg%3E");
      }
    }
  }
}

.prevent-cursor {
  cursor: not-allowed;
}
</style>
